import type { ExecutionIncident } from '../routes/execution/incidentFacade';
import type { ExecutionProgressHydration } from '../executionOperations/types';
import type { RouteOperationalCoordination } from '../operationalCoordination/types';
import type { RouteOperationalIntelligence } from '../operationalIntelligence/types';
import type { OperationalAttention } from '../operationalDomain/processes/operationalProcessTypes';
import {
  adaptiveStateLabel,
  hasRecentActivity,
  RECOVERY_CONTROLLED_WAIT_HOURS,
  RECOVERY_STABILIZE_ACTIVITY_HOURS
} from './recoveryHeuristics';
import type { RecoveryConfirmationHydration } from '../executionOperations/types';
import { adaptiveDisplaySummary } from './executionStateHierarchy';
import type {
  AdaptiveExecutionState,
  AdaptiveExecutionStateProjection,
  BuildRecoveryParams,
  ExecutionStabilizationProjection,
  IncidentRecoveryProjection,
  RecoveryActionCode,
  RecoveryActionItem,
  RecoveryActionProjection,
  RecoveryPathItem,
  RecoveryPathProjection,
  RecoveryTimelineItem,
  RouteOperationalRecovery,
  StabilizationTrend
} from './types';

function action(
  code: RecoveryActionCode,
  label: string,
  available: boolean,
  reason: string
): RecoveryActionItem {
  return { code, label, available, reason };
}

export function buildRecoveryActionProjection(params: {
  intelligence: RouteOperationalIntelligence | null;
  coordination: RouteOperationalCoordination | null;
  incidents: ExecutionIncident[];
  hydration: ExecutionProgressHydration;
  adaptiveState: AdaptiveExecutionState;
}): RecoveryActionProjection {
  const { intelligence, coordination, incidents, hydration, adaptiveState } = params;
  const openDowntime = incidents.some((i) => i.incidentType === 'downtime' && i.status === 'open');
  const stalled = intelligence?.progression.isStalled ?? false;
  const partial = hydration.routeExecutionProjection?.partialExecution?.hasPartialDelivery ?? false;
  const hasConflict = coordination?.conflicts.hasConflict ?? false;

  const actions: RecoveryActionItem[] = [
    action(
      'confirm_delay',
      'Подтвердить задержку',
      adaptiveState !== 'NORMAL',
      'Фиксация controlled delay без смены execution state'
    ),
    action(
      'confirm_downtime',
      'Подтвердить простой',
      openDowntime,
      'Открытый простой требует подтверждения'
    ),
    action(
      'controlled_waiting',
      'Контролируемое ожидание',
      stalled || openDowntime,
      'Ожидание на точке с операторским контролем'
    ),
    action(
      'resume_execution',
      'Возобновить исполнение',
      stalled && (intelligence?.progression.hasProgression ?? false),
      'Есть база для продолжения после паузы'
    ),
    action(
      'partial_recovery',
      'Частичное восстановление',
      partial,
      'Частичная выгрузка — controlled continuation'
    ),
    action(
      'split_execution_candidate',
      'Кандидат на разделение',
      partial && stalled,
      'Долгая частичная выгрузка'
    ),
    action(
      'delayed_continuation',
      'Отложенное продолжение',
      adaptiveState === 'DELAYED_BUT_CONTROLLED' || adaptiveState === 'RECOVERY_IN_PROGRESS',
      'Продолжение после подтверждённой задержки'
    ),
    action(
      'manual_reroute_candidate',
      'Ручной пересмотр маршрута',
      hasConflict,
      'Конфликт координации — только операторское решение'
    )
  ];

  const available = actions.filter((a) => a.available);
  const primaryAction = available[0]?.code ?? null;

  return { actions: actions.filter((a) => a.available || a.code === 'confirm_delay').slice(0, 6), primaryAction };
}

export function buildAdaptiveExecutionStateProjection(params: {
  intelligence: RouteOperationalIntelligence | null;
  coordination: RouteOperationalCoordination | null;
  stabilization: ExecutionStabilizationProjection;
  incidents: ExecutionIncident[];
  confirmations?: RecoveryConfirmationHydration[];
}): AdaptiveExecutionStateProjection {
  const { intelligence, coordination, stabilization, incidents, confirmations = [] } = params;
  const health = intelligence?.health.state;
  const openDowntime = incidents.some((i) => i.incidentType === 'downtime' && i.status === 'open');
  const openBlocking = incidents.some((i) => i.status === 'open' && i.blockingFlag);

  let state: AdaptiveExecutionState = 'NORMAL';

  if (health === 'CRITICAL' || health === 'BLOCKED' || openBlocking) {
    state = 'CRITICAL_RECOVERY';
  } else if (stabilization.trend === 'improving') {
    state = 'STABILIZING';
  } else if (stabilization.trend === 'degrading' || coordination?.cascade.hasCascadeRisk) {
    state = 'DEGRADED';
  } else if (intelligence?.progression.isStalled) {
    state = openDowntime ? 'DELAYED_BUT_CONTROLLED' : 'RECOVERY_IN_PROGRESS';
  } else if (health === 'DELAYED' || health === 'STALLED') {
    state = openDowntime ? 'DELAYED_BUT_CONTROLLED' : 'RECOVERY_IN_PROGRESS';
  }

  if (health === 'HEALTHY' && !openDowntime) {
    state = 'NORMAL';
  }

  const hasConfirmedDelay = confirmations.some((c) => c.active && c.type === 'confirm_delay');
  const hasConfirmedWaiting = confirmations.some(
    (c) => c.active && (c.type === 'confirm_waiting' || c.type === 'confirm_downtime')
  );

  let overlayNote: string | null = null;
  if (state === 'STABILIZING') {
    overlayNote = 'Исполнение стабилизируется';
  } else if (state === 'CRITICAL_RECOVERY') {
    overlayNote = 'Требуется решение оператора';
  } else if (state === 'DELAYED_BUT_CONTROLLED' && openDowntime) {
    overlayNote = 'Ожидание разгрузки под операторским контролем';
  }

  const display = adaptiveDisplaySummary({
    healthLabel: intelligence?.health.label ?? null,
    adaptiveLabel: adaptiveStateLabel(state),
    overlayNote,
    hasConfirmedDelay,
    hasConfirmedWaiting
  });

  return {
    state,
    label: adaptiveStateLabel(state),
    summary: display.summary,
    overlayNote: display.detail
  };
}

export function buildRecoveryPathProjection(params: {
  actions: RecoveryActionProjection;
  intelligence: RouteOperationalIntelligence | null;
  coordination: RouteOperationalCoordination | null;
  hydration: ExecutionProgressHydration;
  adaptiveState: AdaptiveExecutionState;
  confirmations?: RecoveryConfirmationHydration[];
}): RecoveryPathProjection {
  const paths: RecoveryPathItem[] = [];
  const { intelligence, coordination, hydration, adaptiveState, confirmations = [] } = params;

  const push = (code: RecoveryPathItem['code'], title: string, detail: string, recommended: boolean) => {
    paths.push({ code, title, detail, recommended });
  };

  if (!hydration.progress.executionComplete) {
    push(
      'continue_route',
      'Продолжить маршрут',
      'Штатное продолжение после стабилизации',
      adaptiveState === 'STABILIZING' || adaptiveState === 'NORMAL'
    );
  }

  if (hydration.routeExecutionProjection?.partialExecution?.hasPartialDelivery) {
    push(
      'partial_complete',
      'Частично завершить',
      'Controlled partial completion',
      intelligence?.risks.risks.some((r) => r.code === 'partial_unload_stagnation') ?? false
    );
  }

  if (intelligence?.progression.isStalled) {
    push(
      'postpone_unload',
      'Перенести выгрузку (оператор)',
      'Без автоматического изменения плана',
      false
    );
    push(
      'controlled_continuation',
      'Контролируемое продолжение',
      'Продолжение после подтверждённой паузы',
      adaptiveState === 'DELAYED_BUT_CONTROLLED' || adaptiveState === 'RECOVERY_IN_PROGRESS'
    );
  }

  if (
    confirmations.some((c) => c.active && c.type === 'confirm_waiting') ||
    params.actions.actions.some((a) => a.code === 'controlled_waiting' && a.available)
  ) {
    push('waiting_confirmed', 'Ожидание подтверждено', 'Controlled waiting active', true);
  }

  if (coordination?.conflicts.hasConflict) {
    push(
      'manual_reroute_candidate',
      'Ручной пересмотр назначения',
      coordination.conflicts.conflicts[0]?.detail ?? 'Конфликт координации',
      true
    );
  }

  const recommended = paths.filter((p) => p.recommended);
  return {
    paths: paths.slice(0, 5),
    primaryPath: recommended[0]?.code ?? paths[0]?.code ?? null
  };
}

export function buildExecutionStabilizationProjection(params: {
  intelligence: RouteOperationalIntelligence | null;
  coordination: RouteOperationalCoordination | null;
  incidents: ExecutionIncident[];
  hydration: ExecutionProgressHydration;
  now: Date;
}): ExecutionStabilizationProjection {
  const { intelligence, coordination, incidents, hydration, now } = params;
  const factors: string[] = [];

  const resolvedDowntime = incidents.some(
    (i) => i.incidentType === 'downtime' && i.status === 'resolved'
  );
  const openDowntime = incidents.some((i) => i.incidentType === 'downtime' && i.status === 'open');

  if (resolvedDowntime && !openDowntime) {
    factors.push('Простой подтверждён — давление снижено');
  }

  const recentUnload = hasRecentActivity(
    hydration.stops.flatMap((s) => [s.unloadingStartedAt, s.unloadingCompletedAt]),
    now,
    RECOVERY_STABILIZE_ACTIVITY_HOURS
  );
  if (recentUnload) {
    factors.push('Выгрузка возобновлена — риск застоя снижен');
  }

  const inactive = intelligence?.progression.hoursSinceLastActivity;
  if (inactive != null && inactive >= RECOVERY_CONTROLLED_WAIT_HOURS) {
    factors.push(`Повторная задержка ${Math.round(inactive)} ч — деградация`);
  }

  if (coordination?.conflicts.hasConflict) {
    factors.push('Конфликт координации усиливает деградацию');
  }

  let trend: StabilizationTrend = 'stable';
  if (intelligence?.progression.cargoFlowMoving || recentUnload) {
    trend = 'improving';
  } else if (intelligence?.progression.isStalled || coordination?.cascade.hasCascadeRisk) {
    trend = 'degrading';
  }

  const pressureReduced = resolvedDowntime && !openDowntime;
  const degradationRisk = trend === 'degrading';

  const label =
    trend === 'improving'
      ? 'Стабилизация улучшается'
      : trend === 'degrading'
        ? 'Деградация усиливается'
        : 'Стабильно';

  return {
    trend,
    label,
    pressureReduced,
    degradationRisk,
    factors: factors.slice(0, 4)
  };
}

export function buildIncidentRecoveryProjection(params: {
  incidents: ExecutionIncident[];
  hydration: ExecutionProgressHydration;
  now: Date;
}): IncidentRecoveryProjection {
  const items = params.incidents.map((inc) => {
    let status: 'active' | 'acknowledged' | 'resolved' = 'active';
    if (inc.status === 'resolved') status = 'resolved';
    else if (inc.status === 'open' && !inc.blockingFlag) status = 'acknowledged';

    const label =
      inc.incidentType === 'downtime'
        ? 'Простой'
        : inc.incidentType === 'breakdown'
          ? 'Поломка'
          : 'Инцидент';

    return {
      incidentId: inc.id,
      label,
      status,
      detail: inc.status === 'resolved' ? 'Закрыт' : inc.blockingFlag ? 'Блокирует' : 'Открыт'
    };
  });

  const unloadResumed = hasRecentActivity(
    params.hydration.stops.flatMap((s) => [s.unloadingStartedAt, s.unloadingCompletedAt]),
    params.now,
    RECOVERY_STABILIZE_ACTIVITY_HOURS
  );

  return {
    items: items.slice(0, 5),
    hasActiveBlocking: params.incidents.some((i) => i.status === 'open' && i.blockingFlag),
    unloadResumed
  };
}

export function buildRecoveryAttentions(
  routeId: number,
  adaptive: AdaptiveExecutionStateProjection,
  actions: RecoveryActionProjection,
  stabilization: ExecutionStabilizationProjection,
  paths: RecoveryPathProjection
): OperationalAttention[] {
  const items: OperationalAttention[] = [];
  let idx = 0;

  if (adaptive.state === 'CRITICAL_RECOVERY') {
    items.push({
      id: `recovery:critical:${routeId}:${idx++}`,
      severity: 'critical',
      source: 'execution',
      relatedProcessId: null,
      relatedProcessType: null,
      blocking: true,
      requiresAction: true,
      title: 'Требуется recovery confirmation',
      detail: adaptive.overlayNote ?? adaptive.summary,
      expiresAt: null,
      ownerUserId: null,
      assignedDepartment: 'OPERATIONS',
      escalationLevel: 1,
      acknowledged: false,
      acknowledgedAt: null
    });
  }

  if (actions.actions.some((a) => a.code === 'confirm_downtime' && a.available)) {
    items.push({
      id: `recovery:downtime:${routeId}:${idx++}`,
      severity: 'warning',
      source: 'execution',
      relatedProcessId: null,
      relatedProcessType: null,
      blocking: false,
      requiresAction: true,
      title: 'Подтвердить простой',
      detail: 'Downtime требует операторского подтверждения',
      expiresAt: null,
      ownerUserId: null,
      assignedDepartment: 'OPERATIONS',
      escalationLevel: 0,
      acknowledged: false,
      acknowledgedAt: null
    });
  }

  if (stabilization.trend === 'degrading' && adaptive.state !== 'NORMAL') {
    items.push({
      id: `recovery:degrade:${routeId}:${idx++}`,
      severity: 'warning',
      source: 'execution',
      relatedProcessId: null,
      relatedProcessType: null,
      blocking: false,
      requiresAction: true,
      title: 'Recovery stalled',
      detail: stabilization.factors[0] ?? 'Деградация',
      expiresAt: null,
      ownerUserId: null,
      assignedDepartment: 'OPERATIONS',
      escalationLevel: 0,
      acknowledged: false,
      acknowledgedAt: null
    });
  }

  if (!paths.primaryPath && adaptive.state !== 'NORMAL' && adaptive.state !== 'STABILIZING') {
    items.push({
      id: `recovery:path:${routeId}:${idx++}`,
      severity: 'warning',
      source: 'execution',
      relatedProcessId: null,
      relatedProcessType: null,
      blocking: false,
      requiresAction: true,
      title: 'Выберите recovery path',
      detail: 'Нет рекомендованного пути восстановления',
      expiresAt: null,
      ownerUserId: null,
      assignedDepartment: 'OPERATIONS',
      escalationLevel: 0,
      acknowledged: false,
      acknowledgedAt: null
    });
  }

  return items.slice(0, 3);
}

export function buildRecoveryTimelineNarrative(params: {
  adaptive: AdaptiveExecutionStateProjection;
  stabilization: ExecutionStabilizationProjection;
  incidentRecovery: IncidentRecoveryProjection;
  actions: RecoveryActionProjection;
}): RecoveryTimelineItem[] {
  const items: RecoveryTimelineItem[] = [];

  if (params.adaptive.state === 'DELAYED_BUT_CONTROLLED') {
    items.push({ key: 'rec-delay', label: 'Задержка под контролем', kind: 'recovery' });
  }
  if (params.actions.actions.some((a) => a.code === 'confirm_downtime' && a.available)) {
    items.push({ key: 'rec-downtime', label: 'Требуется подтверждение простоя', kind: 'recovery' });
  } else if (params.stabilization.pressureReduced) {
    items.push({ key: 'rec-downtime-ok', label: 'Простой подтверждён', kind: 'recovery' });
  }
  if (params.adaptive.state === 'RECOVERY_IN_PROGRESS') {
    items.push({ key: 'rec-started', label: 'Восстановление начато', kind: 'recovery' });
  }
  if (params.incidentRecovery.unloadResumed) {
    items.push({ key: 'rec-unload', label: 'Выгрузка возобновлена', kind: 'recovery' });
  }
  if (params.adaptive.state === 'STABILIZING') {
    items.push({ key: 'rec-stable', label: 'Исполнение стабилизируется', kind: 'recovery' });
  }
  if (params.actions.actions.some((a) => a.code === 'controlled_waiting' && a.available)) {
    items.push({ key: 'rec-wait', label: 'Контролируемое ожидание', kind: 'recovery' });
  }

  return items.slice(0, 5);
}

export function buildCoordinationRecoveryNote(
  coordination: RouteOperationalCoordination | null,
  stabilization: ExecutionStabilizationProjection
): string | null {
  if (!coordination) return null;
  if (stabilization.pressureReduced && coordination.vehicleChain.impactedNextCount > 0) {
    return 'Задержка стабилизирована — влияние на следующий рейс снижено';
  }
  if (stabilization.degradationRisk && coordination.conflicts.hasConflict) {
    return 'Recovery не удался — конфликт занятости усилился';
  }
  return null;
}

export function buildRouteOperationalRecovery(
  params: BuildRecoveryParams
): RouteOperationalRecovery | null {
  if (!params.hydration.routeExecutionProjection) return null;

  const now = params.now ?? new Date();
  const incidents = params.incidents ?? [];

  const stabilization = buildExecutionStabilizationProjection({
    intelligence: params.intelligence,
    coordination: params.coordination,
    incidents,
    hydration: params.hydration,
    now
  });

  const confirmations = params.confirmations ?? params.hydration.recoveryConfirmations ?? [];

  const adaptiveState = buildAdaptiveExecutionStateProjection({
    intelligence: params.intelligence,
    coordination: params.coordination,
    stabilization,
    incidents,
    confirmations
  });

  const actions = buildRecoveryActionProjection({
    intelligence: params.intelligence,
    coordination: params.coordination,
    incidents,
    hydration: params.hydration,
    adaptiveState: adaptiveState.state
  });

  const paths = buildRecoveryPathProjection({
    actions,
    intelligence: params.intelligence,
    coordination: params.coordination,
    hydration: params.hydration,
    adaptiveState: adaptiveState.state,
    confirmations
  });

  const incidentRecovery = buildIncidentRecoveryProjection({
    incidents,
    hydration: params.hydration,
    now
  });

  const recoveryAttentions = buildRecoveryAttentions(
    params.hydration.routeId,
    adaptiveState,
    actions,
    stabilization,
    paths
  );

  const recoveryTimeline = buildRecoveryTimelineNarrative({
    adaptive: adaptiveState,
    stabilization,
    incidentRecovery,
    actions
  });

  const coordinationNote = buildCoordinationRecoveryNote(params.coordination, stabilization);

  const recoveryParts: string[] = [];
  if (adaptiveState.overlayNote) recoveryParts.push(adaptiveState.overlayNote);
  if (stabilization.pressureReduced) recoveryParts.push('Давление снижено');
  if (coordinationNote) recoveryParts.push(coordinationNote);

  return {
    routeId: params.hydration.routeId,
    actions,
    adaptiveState,
    paths,
    stabilization,
    incidentRecovery,
    recoveryAttentions,
    recoveryTimeline,
    recoverySummary: recoveryParts[0] ?? (adaptiveState.state !== 'NORMAL' ? adaptiveState.summary : null),
    coordinationNote
  };
}
